
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TeacherDetailProfile {
  teacher_code: string;
  full_name: string;
  nickname: string;
  phone_number: string;
  teaching_experience: string;
  academic_achievements: string;
  status: string;
  subjects: any[];
  specialized: Array<{
    name: string;
    description?: string | null;
  }>;
}

export interface TeacherMonthlyReport {
  month: number;
  year: number;
  total_reports: number;
  approved_worksheet_reports: number;
  total_salary: number;
  commission_amount: number;
  confirmation_status: string;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeacherAvailabilityTemplate {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_working_day: boolean;
  slot_order: number;
}

export interface TeacherAvailabilityException {
  id: string;
  exception_date: string;
  exception_type: string;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  is_active: boolean;
}

export interface TeacherDetailData {
  profile: TeacherDetailProfile;
  monthlyReports: TeacherMonthlyReport[];
  availabilityTemplates: TeacherAvailabilityTemplate[];
  availabilityExceptions: TeacherAvailabilityException[];
}

export const useTeacherDetail = (teacherCode: string) => {
  return useQuery({
    queryKey: ['teacher-detail', teacherCode],
    queryFn: async (): Promise<TeacherDetailData> => {
      // Fetch teacher profile
      const { data: profile, error: profileError } = await supabase
        .from('teacher_profiles_view')
        .select('*')
        .eq('teacher_code', teacherCode)
        .single();

      if (profileError) {
        console.error('Error fetching teacher profile:', profileError);
        throw profileError;
      }

      // Fetch monthly reports (using teacher_code as teacher_id)
      const { data: monthlyReports, error: reportsError } = await supabase
        .from('teacher_monthly_reports')
        .select('*')
        .eq('teacher_id', teacherCode)
        .order('year', { ascending: false })
        .order('month', { ascending: false });

      if (reportsError) {
        console.error('Error fetching monthly reports:', reportsError);
        throw reportsError;
      }

      // Fetch availability templates
      const { data: availabilityTemplates, error: templatesError } = await supabase
        .from('teacher_availability_templates')
        .select('*')
        .eq('teacher_code', teacherCode)
        .order('day_of_week', { ascending: true })
        .order('slot_order', { ascending: true });

      if (templatesError) {
        console.error('Error fetching availability templates:', templatesError);
        throw templatesError;
      }

      // Fetch availability exceptions
      const { data: availabilityExceptions, error: exceptionsError } = await supabase
        .from('teacher_availability_exceptions')
        .select('*')
        .eq('teacher_code', teacherCode)
        .order('exception_date', { ascending: false });

      if (exceptionsError) {
        console.error('Error fetching availability exceptions:', exceptionsError);
        throw exceptionsError;
      }

      // Transform the data to match our interfaces
      const transformedProfile: TeacherDetailProfile = {
        teacher_code: profile.teacher_code || '',
        full_name: profile.full_name || '',
        nickname: profile.nickname || '',
        phone_number: profile.phone_number || '',
        teaching_experience: profile.teaching_experience || '',
        academic_achievements: profile.academic_achievements || '',
        status: profile.status || '',
        subjects: Array.isArray(profile.subjects) ? profile.subjects : [],
        specialized: Array.isArray(profile.specialized)
          ? profile.specialized.map((spec: any) => ({
              name: spec?.name || '',
              description: spec?.description || null
            }))
          : []
      };

      return {
        profile: transformedProfile,
        monthlyReports: monthlyReports as TeacherMonthlyReport[],
        availabilityTemplates: availabilityTemplates as TeacherAvailabilityTemplate[],
        availabilityExceptions: availabilityExceptions as TeacherAvailabilityException[]
      };
    },
    enabled: !!teacherCode,
  });
};
